import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

const PALETTE = {
  primaryRed: "#D84E55",
  accentBlue: "#3A86FF",
  border: "#E5E7EB",
};

const Container = ({ children }) => (
  <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
);

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold bg-white">
    {children}
  </span>
);

const SectionCard = ({ id, title, children }) => (
  <section id={id} className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 shadow-sm">
    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{title}</h2>
    <div className="mt-3 text-[15px] leading-7 text-gray-700">{children}</div>
  </section>
);

const RefundPolicy = () => {
  const lastUpdated = useMemo(() => "5 Apr 2026", []);

  useEffect(() => {
    document.title = "Refund & Cancellation Policy | Routesbook.lk";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div
        className="w-full border-b"
        style={{
          borderColor: PALETTE.border,
          background:
            "linear-gradient(135deg, rgba(216,78,85,0.10) 0%, rgba(58,134,255,0.10) 100%)",
        }}
      >
        <Container>
          <div className="py-10 sm:py-12">
            <div className="flex items-center gap-3 text-sm">
              <Link to="/" className="text-blue-700 hover:underline">
                Home
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700">Refund Policy</span>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
                  Refund & Cancellation Policy
                </h1>
                <p className="mt-2 text-sm sm:text-base text-gray-600">
                  Clear rules for cancellations, refunds, failed payments, and operator-related trip cancellations.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Badge>Last updated: {lastUpdated}</Badge>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        {/* Intro */}
        <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Routesbook.lk Refund Policy</div>
              <p className="mt-2 text-sm text-gray-700">
                This policy explains how cancellations, refunds, failed payments, and operator cancellations are handled
                for bookings made through <b>Routesbook.lk</b>, operated by <b>NDD IT Solutions</b>.
              </p>
            </div>
            <Link
              to="/terms"
              className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: PALETTE.primaryRed }}
            >
              View Terms
            </Link>
          </div>
        </div>

        {/* TOC */}
        <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-sm">
          <div className="text-sm font-semibold text-gray-900">On this page</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {[
              ["overview", "Overview"],
              ["passenger-cancel", "Passenger cancellation"],
              ["operator-cancel", "Operator cancellation"],
              ["failed", "Failed payments"],
              ["how", "How refunds are processed"],
              ["fees", "Convenience / service fees"],
              ["exceptions", "Non-refundable situations"],
              ["contact", "Contact for cancellations/refunds"],
            ].map(([id, label]) => (
              <a key={id} href={`#${id}`} className="hover:underline" style={{ color: PALETTE.accentBlue }}>
                {label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-5 pb-16">
          <SectionCard id="overview" title="1) Overview">
            <p>
              This policy applies to tickets booked through Routesbook.lk. Routesbook.lk acts as a booking platform,
              while the transport service itself is provided by the relevant bus operator. As a result, some cancellations,
              schedule changes, and refund decisions may depend on the operator’s rules, trip status, and applicable law.
            </p>
          </SectionCard>

          <SectionCard id="passenger-cancel" title="2) Passenger cancellation (before departure)">
            <p className="mb-3">
              If cancellation is allowed for your booking, refunds may be calculated based on the remaining time before the
              scheduled departure time of the trip.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 border-b">Time before departure</th>
                    <th className="text-left p-3 border-b">Eligible refund</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3">More than 24 hours</td>
                    <td className="p-3">Up to 90% of the ticket fare, excluding non-refundable service charges where applicable</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">12 to 24 hours</td>
                    <td className="p-3">Up to 75% of the ticket fare</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">6 to 12 hours</td>
                    <td className="p-3">Up to 50% of the ticket fare</td>
                  </tr>
                  <tr>
                    <td className="p-3">Less than 6 hours / No-show</td>
                    <td className="p-3">No refund</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-sm text-gray-700">
              <b>Important:</b> If a different cancellation policy is shown during booking, on the ticket, or is imposed
              by the bus operator for a specific service, that specific policy will apply to that booking.
            </p>
          </SectionCard>

          <SectionCard id="operator-cancel" title="3) Operator cancellation / trip not operated">
            <ul className="list-disc pl-5 space-y-2">
              <li>
                If the operator cancels the trip and the service is not operated, eligible passengers will normally receive
                a refund of the paid ticket fare.
              </li>
              <li>
                If the operator provides an acceptable alternative trip, reschedule option, or replacement arrangement,
                passengers may choose to accept that option where available.
              </li>
              <li>
                If the trip cannot be provided as booked and no suitable alternative is accepted, a refund may be processed
                subject to verification of the booking and payment.
              </li>
            </ul>
          </SectionCard>

          <SectionCard id="failed" title="4) Payment failed / amount deducted">
            <p>
              If your payment attempt is unsuccessful but your bank account, card, or mobile wallet shows a deduction, the
              amount is usually reversed automatically by the payment gateway, bank, or issuer according to their processing timelines.
            </p>
            <p className="mt-3">
              If the amount is not reversed within a reasonable period, please contact us with your booking details,
              payment reference, date, and contact number so we can help review the transaction.
            </p>
          </SectionCard>

          <SectionCard id="how" title="5) How refunds are processed">
            <ul className="list-disc pl-5 space-y-2">
              <li>Approved refunds are generally processed back to the original payment method where possible.</li>
              <li>Refund timelines depend on the payment gateway, acquiring bank, card issuer, or wallet provider.</li>
              <li>Processing may take several business days depending on the payment channel used.</li>
              <li>We may request a booking ID, ticket ID, payment reference, and supporting details to verify the refund request.</li>
              <li>Refund status may remain subject to operator confirmation in cases involving trip disputes or service issues.</li>
            </ul>
          </SectionCard>

          <SectionCard id="fees" title="6) Convenience / service fees">
            <p>
              Where a convenience fee, service charge, booking fee, or processing fee is applied, that portion may be
              non-refundable unless the trip is cancelled by the operator, the transaction is reversed due to a failed payment,
              or a refund is otherwise required under applicable law.
            </p>
          </SectionCard>

          <SectionCard id="exceptions" title="7) Non-refundable situations">
            <ul className="list-disc pl-5 space-y-2">
              <li>No refund may be available for no-shows or missed departures.</li>
              <li>No refund may be available once the trip has already departed, unless required by law or approved by the operator.</li>
              <li>Incorrect passenger information submitted by the customer may delay or invalidate refund eligibility in some cases.</li>
              <li>Refunds may be refused where there is evidence of policy abuse, fraud, chargeback misuse, or unauthorized activity.</li>
            </ul>
          </SectionCard>

          <SectionCard id="contact" title="8) Contact for cancellations/refunds">
            <p>
              For refund or cancellation support, please contact:
            </p>
            <p className="mt-3">
              Email:{" "}
              <a className="font-semibold hover:underline" href="mailto:support@routesbook.lk" style={{ color: PALETTE.accentBlue }}>
                support@routesbook.lk
              </a>
            </p>
            <p className="mt-2">
              Business name: <b>NDD IT Solutions</b>
              <br />
              Address: <b>Lenaduwa Junction, Godagama, Palatuwa, Matara, Sri Lanka</b>
              <br />
              Phone: <b>0767037547</b>
            </p>
          </SectionCard>

          <div className="text-xs text-gray-500">
            This page is provided for general informational purposes and does not constitute legal advice.
          </div>
        </div>
      </Container>
    </div>
  );
};

export default RefundPolicy;
